import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row as RowBootstrap, Button } from 'react-bootstrap';
import Row from "../../../SDFormTemplate/Row";
import CustomSection from '../../../SDFormTemplate/CustomSection';
import Accordion from 'components/Common/Accordion/Accordion';

import { getCompanyIdFromRoute } from 'services/redux/selectors/company';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from 'components/Common/Tooltip/Tooltip';

import Icon from 'components/Icons/Icon';
import Alert from 'components/Common/Alert';

import {
  loadCompanyCreateEnterpriseStart,
} from "services/redux/actions/serviceDesk";

import './Sites.scss'
import SitesCheckboxTable from './SitesCheckboxTable';
import CustomGroupSelect from './Groups/CustomGroupSelect';

// sudo site search and selection component
  const SiteSelection = ({ 
    companyId, 
    tableFilters,
    updateTableFilters,
    sudoSites,
    selection,
    selectAll,
    handleSelection,
    handleSelectAll,
    getDataTableState,
  }) => {

    return (
      <div style={{ minHeight: '300px' }}>
        <RowBootstrap className='d-flex align-items-center flex-RowBootstrap px-3 mt-2 mb-0'>
          <Col className={`d-flex flex-column p-0`}>
            <span className='d-flex flex-column'>
              <p>Search for and select the site(s) to associate with this enterprise:</p>
            </span>

            <SitesCheckboxTable
              companyId={companyId}
              tableFilters={tableFilters}
              updateTableFilters={updateTableFilters}
              sudoSites={sudoSites}
              selection={selection}
              selectAll={selectAll}
              handler={getDataTableState}
              handleSelection={handleSelection}
              handleSelectAll={handleSelectAll}
            />
          </Col>
        </RowBootstrap>
      </div>
    )
  }

  function SingleAssignment({ id, sites_count, selectedGroups, flattenedGroups, handleSelectedGroups, handleDeleteAssignments }) {

    return (
      <RowBootstrap className='d-flex align-content-center justify-content-center px-4 mb-2 h-auto' id='single-assignment-component'>
        <Col className='d-flex align-content-center justify-content-center col-1 id-column'>
          <span className='d-flex align-items-center justify-content-center'>{id}</span>
        </Col>

        <Col
          style={{
            padding: '8px',
            borderTop: 'solid 1px lightgray',
            borderBottom: 'solid 1px lightgray',
          }}
        >
          <RowBootstrap>
            <Col className='d-flex align-content-center justify-content-center w-100'>
              <span className='d-flex align-items-center justify-content-start pl-1 w-100'>Sites in selection: {sites_count}</span>
            </Col>
            
            <Col className='d-flex align-content-center w-100'>
            
              <CustomGroupSelect
                groups={flattenedGroups}
                selectedGroups={selectedGroups}
                handleSelectedGroups={handleSelectedGroups}
                assignmentId={id}
              />

            </Col>
          </RowBootstrap>
        </Col>
        <span className='ml-0 pl-0'>
          {
            <Button
              variant='outline-danger'
              className='h-100'
              onClick={() => handleDeleteAssignments(id)}
              style={{
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
              }}
            >
            <Icon 
              className='fas fa-trash'
            />
          </Button>
          }
        </span>
      </RowBootstrap>
    )
  }

function GroupAssignment({ 
  setSelection, 
  siteGroupAssignment, 
  setSiteGroupAssignment, 
  selection, 
  groups, 
  setSelectAll, 
  selectAll, 
  dataTableState, 
  formatDataTableStateParams,
  handleSetCounter,
  idCounter,
  totalCount,
  setTotalCount,
}) {

  const darkMode = useSelector(state => state.app.darkMode)

  const handleSelectedGroups = (assignmentId, updatedGroups) => {
    setSiteGroupAssignment((prev) =>
      prev.map((sg) =>
        sg.id === assignmentId ? { ...sg, groups: updatedGroups ?? [] } : sg
      )
    );
  };

  const handleDeleteAssignments = (id = null) => {
    const type = id ? `assignment id: <span class='font-weight-bold'>${id}</span>` : `<span class='font-weight-bold'>all</span> site-group assignments`;

    Alert({
      html: `<div>Are you sure you want to delete ${type}?</div>`,
      showCancelButton: true,
      confirmButtonText: `Delete ${!id ? 'all' : 'Assignment ' + id}`,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        if (id) {
          setSiteGroupAssignment(() => {
            const copy = [...siteGroupAssignment];
            return copy?.filter(sg => sg?.id !== id);
          })
          return;
        }
        setSiteGroupAssignment([])
        return;
      } else {
        return;
      }
    })
  }

  const flattenTree = useCallback((nodes, parent_id = null, depth = 0) => {
    return nodes.reduce((acc, node) => {
      const { children, name, id, ...rest } = node;
      const isParent = !!children?.length || node.isParent;
      const prefixedName = '_'.repeat(depth) + name;
      acc.push({
        ...rest,
        id,
        name: prefixedName,
        parent_id,
        isParent,
        label: prefixedName,
        value: id,
      });
      if (children?.length) acc.push(...flattenTree(children, node.id, depth + 1));
      return acc;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const flattenedGroups = flattenTree(groups);
  
  return (
    <div id='group-assignment-component'>
      {/* Add singular site-selection array to state object */}
      <Tooltip
        description='Add Currently Selected Sites'
        placement='top'
      >
        <Button
          className='d-flex justify-content-center align-items-center w-100 p-3 my-3'
          variant={darkMode ? 'light' : 'primary'}
          style={{
            maxHeight: '20px',
          }}
          disabled={!selectAll && !selection?.length}
          onClick={() => {
            setSiteGroupAssignment((prev) => [
              ...prev,
              {
                id: idCounter + 1,
                sites: selection,
                sites_count: totalCount,
                groups: [],
                select_all: selectAll,
                query_params: formatDataTableStateParams(dataTableState),
              },
            ]);
            handleSetCounter();
            setSelection([]);
            setSelectAll(false);
            setTotalCount(null);
          }}
        >
          <Icon className='fas fa-plus' />
        </Button>
      </Tooltip>

      {/* Selected Sites List */}
      <div
        className={`d-flex justify-content-center align-items-center w-100 p-3 ${!siteGroupAssignment?.length ? 'disabled' : ''}`}
        style={{
          border: 'solid 1px lightgray',
          borderRadius: '5px',
        }}
      >
        <span className='w-100'>
          <span
            className='d-flex justify-content-between align-items-center px-2 pb-1'
            style={{
              borderBottom: 'solid 2px lightgray',
            }}
          >
            <h6 className='d-flex align-items-center'>Assign Sites to Groups</h6>
            {!siteGroupAssignment?.length ?
              <h6>- No sites have been selected -</h6>
            :
              <Button variant="outline-danger" onClick={() => handleDeleteAssignments()}>Clear All Assignments</Button>
            }
          </span>
          <RowBootstrap className='d-flex align-items-center flex-RowBootstrap px-3 mt-3 mb-0'>
            <Col className='w-100 p-0 m-0'>
              {siteGroupAssignment?.map((sg) => (
                <SingleAssignment
                  key={sg.id}
                  id={sg.id}
                  sites_count={sg.sites_count}
                  selectedGroups={sg.groups}
                  flattenedGroups={flattenedGroups}
                  handleSelectedGroups={handleSelectedGroups}
                  handleDeleteAssignments={handleDeleteAssignments}
                />
              ))}
            </Col>
          </RowBootstrap>
        </span>
      </div>
    </div>
  )
}

function Sites({ setInvalidConditions, formData, setFormData, groups }) {

  const dispatch = useDispatch();

  const selectionCount = useSelector((state) => state.serviceDesk.createEnterpriseCompanySites?.data?.pagination?.count);

  const companyId = useSelector(getCompanyIdFromRoute);
  const sudoSites = useSelector((state) => state.serviceDesk.createEnterpriseCompanySites.data.sudoSites);
  const loadingCompanyCreateEnterpriseSites = useSelector((state) => state.serviceDesk.createEnterpriseCompanySites.loadingCompanyCreateEnterpriseSites);
  
  const [siteGroupAssignment, setSiteGroupAssignment] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [tableFilters, setTableFilters] = useState([]);
  const [selection, setSelection] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalCount, setTotalCount] = useState('');
  const [dataTableState, setDataTableState] = useState({})
  const [sudoSitesEmpty] = useState(Array.isArray(sudoSites) && sudoSites.length <= 0);
  
  // handles id counter for site-group assignments 
  // - increments on each new assignment,
  //   resets to 0 if all assignments are deleted
  const [idCounter, setIdCounter] = useState(0);

  useEffect(() => {
    if (!selectAll) {
      setTotalCount(selection?.length || 0);
      return;
    }

    selectionCount && setTotalCount(selectionCount);
    return;
  }, [selectionCount, selectAll, selection])
  
  useEffect(() => {
    if (siteGroupAssignment.length === 0) {
      setIdCounter(0);
    }
  }, [siteGroupAssignment]);

  const handleSetCounter = useCallback(() => {
    setIdCounter(prev => prev + 1);
  }, []);
  // -------------------------------------------|

  const formatDataTableStateParams = useCallback((state) => {

    if (!companyId || !state || Object.keys(state).length === 0) return;

    const limit = state.pageSize || 20;
    const page = state.requestPageNumber || 1;
    
    let paramString = "";
    state?.filtered && state.filtered.forEach((filter) => {
      paramString = `${paramString}&${filter.id}=${encodeURIComponent(filter?.value)}`
    });
    state?.externalFilters && state.externalFilters.forEach((externalFilter) => {
      paramString = `${paramString}&${externalFilter.id}=${encodeURIComponent(externalFilter?.value)}`
    });

    return `?limit=${limit}&page=${page}${paramString}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTableState, companyId])

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      sudo_site_ids: prev.sudo_site_ids?.map((s) =>
        s.id ? 
        { 
          ...s,
          select_all: selectAll,
          query_params: formatDataTableStateParams(dataTableState),
        } : s
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatDataTableStateParams, dataTableState, selectAll])

  const handleSelection = useCallback((sites) => {
    if (selectAll && !sites?.length) {
      setSelection(['selectAll']);
      return;
    }
    setSelection(sites);
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll, selection])
  
  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (selectAll) {
      setSelection([])
    }
  }
  
  const getDataTableState = (state) => {
    setDataTableState(state);
  }

  const updateTableFilters = (newFilters) => {
    setTableFilters(newFilters);
  }

  useEffect(() => {
    const reformattedAssignments = siteGroupAssignment.map((sg) => ({
      ...sg,
      groups: sg.groups?.map((g) => g?.value),
    }));

    setFormData((prev) => ({
      ...prev,
      sudo_site_ids: reformattedAssignments,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteGroupAssignment])

  useEffect(() => {
    if (loadingCompanyCreateEnterpriseSites === null && sudoSitesEmpty) {
      dispatch(loadCompanyCreateEnterpriseStart({ company_id: companyId }));
    }
  }, [loadingCompanyCreateEnterpriseSites, companyId, sudoSitesEmpty, dispatch])

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      sudo_site_ids: selectedSites?.map(site => site?.id) ?? [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSites]);
  
  const title = 'Sites';

  const siteSelectionWrapper = (formData) => (
    <CustomSection
      key='custom_sites_component'
      customContent={
        <SiteSelection 
          key='site_selection_component'
          tableFilters={tableFilters}
          updateTableFilters={updateTableFilters}
          sudoSites={sudoSites}
          companyId={companyId}
          handleSelectAll={handleSelectAll}
          handleSelection={handleSelection}
          selection={selection}
          selectAll={selectAll}
          formData={formData}
          getDataTableState={getDataTableState}
        />
      }
      setInvalidConditions={setInvalidConditions} 
      formData={formData}
      setFormData={setFormData}
    />
  )
  
  const siteGroupAssignmentWrapper = (formData) => {
    return (
      <CustomSection
        key='site_group_custom_section_component'
        customContent={
          <GroupAssignment
            key='site_group_group_assignment'
            sites={selectedSites} 
            setSelectedSites={setSelectedSites} 
            selection={selection} 
            groups={groups} 
            formData={formData}
            setFormData={setFormData}
            siteGroupAssignment={siteGroupAssignment}
            setSiteGroupAssignment={setSiteGroupAssignment}
            setSelection={setSelection}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            dataTableState={dataTableState}
            formatDataTableStateParams={formatDataTableStateParams}
            handleSetCounter={handleSetCounter}
            idCounter={idCounter}
            totalCount={totalCount}
            setTotalCount={setTotalCount}
          />
        }
        title='Sites'
        setInvalidConditions={setInvalidConditions} 
        formData={formData}
        setFormData={setFormData}
        checkInvalid
        required
        conditionProperties={{conditionKey: 'sudo_site_ids', conditionType: 'length', conditionRequirement: ' > 0'}}
      />
    )
  }

  return (
    <div key="sites">
      <Row
        key='row_3'
        sections={[
          (index) => (
            <Accordion
              key='accordion_3'
              title={title}
              accordionHeaderSection={true}
              accordionHeaderContents={{
                size: 'md',
                message: 'Add sites from your Service Desk account to the groups in your Enterprise.',
              }}
              showNumberInHeader={false}
              headerNumber={index + 1}
              content={[
                siteSelectionWrapper(),
                siteGroupAssignmentWrapper(formData),
              ]}
              overflowVisible
            />
          )
        ]}
      />
    </div>
  )
}

export default Sites;
